import React, { useContext, useEffect } from "react";
import {
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  Area, AreaChart, PieChart, Pie, Legend, Cell, BarChart, Bar,
} from "recharts";
import ContentPage from "../Content";
import moment from "moment";
import separateNumber from "../../utils/separateur";
import ExportExcel from "../../utils/ExcelExport";
import { UserContext } from "../../utils/User_check";
import can from "../../utils/Can";
import { useNavigate } from "react-router-dom";
import useEvdDashboard from "../../hooks/useEvdDashboard";
import PageHeader from "../ui/PageHeader";
import StatCard from "../ui/StatCard";
import SectionCard from "../ui/SectionCard";
import EmptyState from "../ui/EmptyState";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

function HomePage() {
  const { user, permissions } = useContext(UserContext);
  const navigate = useNavigate();

  const {
    dateDebut, setDateDebut,
    dateFin, setDateFin,
    dateCapture, setDateCapture,
    loadingStats, loadingAppro, loadingPos, loadingImport,
    TotalAirtime, TotalBundle, bundleViaMomo, TotalCashBack,
    TotalApproDay, approCash, approCredit, tauxAppro, tauxVisite,
    totalVisite, totalAppro,
    dataSecteurAppro, dataSecteurEvd,
    topPos, venteSerie,
    loadDashboard, resetState,
    handleFile, submitImport,
  } = useEvdDashboard();

  const datapie = [
    { name: "BUNDLE VIA MOMO", value: bundleViaMomo },
    { name: "CASH BACK", value: TotalCashBack },
  ];

  useEffect(() => {
    if (user && permissions && !can(permissions, "TABLEAU DE BORD EVD")) {
      navigate("/page-no-found");
    }
  }, [user]);

  useEffect(() => {
    loadDashboard(null, null);
  }, []);

  const periodeLabel = dateFin == null
    ? moment().format("Do MMMM")
    : `${moment(dateDebut).format("Do MMMM")} au ${moment(dateFin).format("Do MMMM")}`;

  const filtre_periode_data = async (e) => {
    e.preventDefault();
    resetState();
    await loadDashboard(dateDebut, dateFin);
  };

  const today_data_fetch = () => {
    setDateDebut(null);
    setDateFin(null);
    window.location.reload();
  };

  return (
    <>
      <ContentPage>
        <PageHeader eyebrow="Rapport EVD" title="Tableau de bord EVD" periodLabel={periodeLabel}>
          <button type="button" className="btn btn-outline-primary btn-icon-text" onClick={today_data_fetch}>
            <i className="mdi mdi-refresh"></i> Actualiser
          </button>
          <button
            type="button"
            className="btn btn-brand btn-icon-text"
            onClick={() => window.$("#exampleModalFiltre").modal("show")}
          >
            <i className="mdi mdi-filter-variant"></i> Filtrer
          </button>
        </PageHeader>

        {/* Totaux principaux */}
        <div className="row">
          <div className="col-sm-6 stretch-card grid-margin">
            <StatCard
              icon="mdi-truck-delivery"
              color="danger"
              label="Total appros commerciaux"
              sublabel={periodeLabel}
              value={separateNumber(TotalApproDay)}
              suffix="FCFA"
              footer="Somme des approvisionnements"
              loading={loadingAppro}
            />
          </div>
          <div className="col-sm-6 stretch-card grid-margin">
            <StatCard
              icon="mdi-cart-outline"
              color="success"
              label="Total vente EVD POS"
              sublabel={periodeLabel}
              value={separateNumber(TotalAirtime + TotalBundle + TotalCashBack)}
              suffix="FCFA"
              footer="Somme des ventes effectuées par les POS"
              loading={loadingAppro}
            />
          </div>
        </div>

        {/* Détails ventes + Pie Chart */}
        <div className="row">
          <div className="col-xl-4 grid-margin d-flex flex-column">
            <div className="mb-3">
              <StatCard
                icon="mdi-cellphone-nfc"
                color="success"
                label="Vente bundle via Momo"
                sublabel={periodeLabel}
                value={separateNumber(bundleViaMomo)}
                suffix="FCFA"
                loading={loadingStats}
              />
            </div>
            <div>
              <StatCard
                icon="mdi-cash-refund"
                color="gold"
                label="Cash Back V2"
                sublabel={periodeLabel}
                value={separateNumber(TotalCashBack)}
                suffix="FCFA"
                loading={loadingStats}
              />
            </div>
          </div>
          <div className="col-xl-8 stretch-card grid-margin">
            <SectionCard icon="mdi-chart-donut" color="primary" title={`Statistique des ventes EVD du ${periodeLabel}`} bodyClassName="p-0 d-flex justify-content-center">
              {!loadingStats ? (
                <PieChart width={620} height={380}>
                  <Pie data={datapie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={120} fill="#8884d8" label>
                    {COLORS.map((color, index) => <Cell key={`cell-${index}`} fill={color} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              ) : (
                <div className="d-flex justify-content-center align-items-center" style={{minHeight:300}}>
                  <div className="spinner-border text-primary" role="status"></div>
                </div>
              )}
            </SectionCard>
          </div>
        </div>

        {/* Appros cash/crédit + PDV */}
        <div className="row">
          <div className="col-sm-4 stretch-card grid-margin">
            <StatCard
              icon="mdi-cash-multiple"
              color="success"
              label="Total appros cash"
              sublabel={periodeLabel}
              value={separateNumber(approCash)}
              suffix="FCFA"
              footer="Somme des approvisionnements cash"
              loading={loadingAppro}
            />
          </div>
          <div className="col-sm-4 stretch-card grid-margin">
            <StatCard
              icon="mdi-credit-card-outline"
              color="danger"
              label="Total appros crédit"
              sublabel={periodeLabel}
              value={separateNumber(approCredit)}
              suffix="FCFA"
              footer="Somme des approvisionnements crédit"
              loading={loadingAppro}
            />
          </div>
          <div className="col-sm-4 stretch-card grid-margin">
            <StatCard
              icon="mdi-store-check-outline"
              color={totalAppro >= 100 ? "success" : "danger"}
              label="Total PDV approvisionnés"
              sublabel={periodeLabel}
              value={loadingAppro ? "" : totalAppro}
              loading={loadingAppro}
              footer={
                <>
                  Pour un total <strong>visite</strong> de{" "}
                  <b className={totalVisite >= 100 ? "text-success-strong" : "text-danger-strong"}>
                    {!loadingAppro ? totalVisite : "…"}
                  </b>
                </>
              }
            />
          </div>
        </div>

        {/* Secteur Appros */}
        <div className="row">
          <div className="col-sm-12 stretch-card grid-margin">
            <SectionCard icon="mdi-chart-bar" color="gold" title={`Statistique des appros par secteurs du ${periodeLabel}`}>
              {!loadingPos ? (
                dataSecteurAppro?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dataSecteurAppro} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" fill="#FF8042" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyState icon="mdi-chart-bar" title="Aucune donnée sur cette période" />
                )
              ) : (
                <div className="d-flex justify-content-center py-5"><div className="spinner-border text-primary" role="status"></div></div>
              )}
            </SectionCard>
          </div>
        </div>

        {/* Secteur EVD */}
        <div className="row">
          <div className="col-sm-12 stretch-card grid-margin">
            <SectionCard icon="mdi-chart-bar" color="primary" title={`Statistique des ventes EVD par secteurs du ${periodeLabel}`}>
              {!loadingPos ? (
                dataSecteurEvd?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dataSecteurEvd} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" fill="#0088FE" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyState icon="mdi-chart-bar" title="Aucune donnée sur cette période" />
                )
              ) : (
                <div className="d-flex justify-content-center py-5"><div className="spinner-border text-primary" role="status"></div></div>
              )}
            </SectionCard>
          </div>
        </div>

        {/* Top 10 POS */}
        <div className="row">
          <div className="col-xl-12 stretch-card grid-margin">
            <SectionCard
              icon="mdi-trophy-award"
              color="gold"
              title={`Top 10 des vendeurs EVD ${periodeLabel}`}
              actions={<ExportExcel excelData={topPos} filename="TOP VENDEURS EVD/POS" title="Télécharger" color="btn-success" />}
            >
              {topPos?.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-striped align-middle">
                    <thead>
                      <tr>
                        <th>NOM DU PDV</th>
                        <th>N° MARCHAND</th>
                        <th>COMMERCIAL</th>
                        <th>N°COMMERCIAL</th>
                        <th className="text-center">MONTANT VENDU</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topPos?.map((pos, index) => (
                        <tr key={index}>
                          <td className="py-1">
                            <img src="../../../assets/images/dashboard/img_3.jpg" alt="" className="rounded-circle me-2" style={{width:28,height:28}} />
                            {pos.NomPDV}
                          </td>
                          <td>{pos.POS_MSISDN}</td>
                          <td>{pos.COMMERCIAL_NAME}</td>
                          <td>{pos.COMMERCIAL_MSISDN}</td>
                          <td className="text-center">
                            <b className="text-danger">{separateNumber(pos.total)}</b>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState icon="mdi-trophy-outline" title="Aucun vendeur sur cette période" />
              )}
            </SectionCard>
          </div>
        </div>

        {/* Evolution annuelle */}
        <div className="row">
          <div className="col-xl-12 stretch-card grid-margin">
            <SectionCard icon="mdi-chart-areaspline" color="primary" title={`Evolution des ventes EVD de l'année ${moment().format("Y")}`}>
              <div style={{ backgroundColor: "#1e1e2f", padding: "30px", borderRadius: "10px" }}>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={venteSerie} margin={{ top: 10, right: 50, left: 50, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorEvd" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#FF9933" stopOpacity={0.8} />
                        <stop offset="100%" stopColor="#FF9933" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#3c3c3c" />
                    <XAxis dataKey="month" tick={{ fill: "#ccc" }} />
                    <YAxis tick={{ fill: "#ccc" }} />
                    <Tooltip contentStyle={{ backgroundColor: "#333", border: "none", color: "#fff" }} />
                    <Legend />
                    <Area type="monotone" dataKey="evd" stroke="#FF9933" fill="url(#colorEvd)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </SectionCard>
          </div>
        </div>

        {/* Modal Import */}
        <div className="modal fade" id="exampleModalCenteContrat" tabIndex="-1" role="dialog" aria-hidden="true">
          <div className="modal-dialog modal-md" role="document">
            <form className="modal-content" onSubmit={submitImport}>
              <div className="modal-header">
                <h5 className="modal-title">Importer un template excel</h5>
                <button type="button" className="btn-close text-danger" onClick={() => window.$("#exampleModalCenteContrat").modal("hide")}>
                  <span aria-hidden="true"></span>
                </button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-3 col-md-12">
                    <label className="form-label">Capture date</label>
                    <input type="date" className="form-control" id="datecapture" onChange={(e) => setDateCapture(e.target.value)} />
                  </div>
                  <div className="mb-3 col-md-12">
                    <label className="form-label">Uploader le fichier</label>
                    <input type="file" className="form-control" required onChange={handleFile} id="file_excel" />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => window.$("#exampleModalCenteContrat").modal("hide")}>
                  Fermer
                </button>
                <button type="submit" className={loadingImport ? "btn btn-primary disabled" : "btn btn-primary"}>
                  {loadingImport ? "Enregistrement en cours ..." : "Enregistrer"}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Modal Filtre */}
        <div className="modal fade" id="exampleModalFiltre" tabIndex="-1" role="dialog" aria-hidden="true">
          <div className="modal-dialog modal-md" role="document">
            <form className="modal-content" onSubmit={filtre_periode_data}>
              <div className="modal-header">
                <h5 className="modal-title">Recherche periodique</h5>
                <button type="button" className="btn-close" onClick={() => window.$("#exampleModalFiltre").modal("hide")}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-3 col-md-12">
                    <label className="form-label">Date debut</label>
                    <input type="date" className="form-control" onChange={(e) => setDateDebut(e.target.value)} />
                  </div>
                  <div className="mb-3 col-md-12">
                    <label className="form-label">Date Fin</label>
                    <input type="date" className="form-control" onChange={(e) => setDateFin(e.target.value)} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                {!loadingStats ? (
                  <button type="submit" className="btn btn-primary">
                    Lancer la recherche
                  </button>
                ) : (
                  <button type="button" className="btn btn-primary disabled form-control">
                    Chargement en cours&nbsp;
                    <div className="spinner-border p-2" role="status"></div>
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </ContentPage>
    </>
  );
}

export default HomePage;
